import React, { useState } from 'react';
import {
  Card, CardHeader, CardContent, List, ListItem, Box, LinearProgress,
  Typography, Divider, Collapse, IconButton, Chip, Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import {
  DischargeDecisionFactor, DischargeDisposition, DISPOSITION_LABELS,
} from '~/types/discharge';

interface Props {
  disposition: DischargeDisposition;
  score: number;
  decisionFactors: DischargeDecisionFactor[];
  isRecommended?: boolean;
  isSelected?: boolean;
  defaultExpanded?: boolean;
  onSelect?: (disposition: DischargeDisposition) => void;
}

function getScoreColor(score: number): string {
  if (score >= 80) return '#1B5E20';
  if (score >= 60) return '#E65100';
  return '#B71C1C';
}

function getScoreBg(score: number): string {
  if (score >= 80) return '#E8F5E9';
  if (score >= 60) return '#FFF3E0';
  return '#FFEBEE';
}

function formatTs(ts: string): string {
  return new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

const DischargeProgramFitCard: React.FC<Props> = ({
  disposition, score, decisionFactors, isRecommended = false, isSelected = false, defaultExpanded = false, onSelect,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const sorted = [...decisionFactors].sort((a, b) => b.importance - a.importance);

  return (
    <Card
      sx={{
        mb: 2,
        border: isSelected ? '2px solid' : '1px solid',
        borderColor: isSelected ? 'success.main' : 'divider',
        backgroundColor: isSelected ? '#E8F5E9' : 'transparent',
        boxShadow: 0,
      }}
    >
      <CardHeader
        onClick={() => setExpanded(!expanded)}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" sx={{ p: 0.25 }} tabIndex={-1}>
                {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              </IconButton>
              <Typography variant="subtitle1" sx={{ fontWeight: isSelected ? 700 : 600, fontSize: '1.1rem' }}>
                {DISPOSITION_LABELS[disposition]}
              </Typography>
              {isSelected && <Chip label="Selected" size="small" color="success" sx={{ fontWeight: 600 }} />}
              {isRecommended && (
                <Chip
                  label="Recommended"
                  size="small"
                  color="primary"
                  variant={isSelected ? 'outlined' : 'filled'}
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
            <Chip
              label={score}
              sx={{
                fontWeight: 700, fontSize: '1rem', height: 32,
                backgroundColor: getScoreBg(score), color: getScoreColor(score),
              }}
            />
          </Box>
        }
        sx={{
          backgroundColor: isSelected ? 'transparent' : 'grey.50',
          pb: 1,
          cursor: 'pointer',
          transition: 'background-color 0.15s ease',
          '&:hover': { backgroundColor: isSelected ? 'rgba(232,245,233,0.6)' : 'grey.100' },
        }}
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
          {sorted.length === 0 ? (
            <Typography color="grey.500">No decision factors available</Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {sorted.map((factor, idx) => {
                const color = factor.contribution === 'positive' ? '#4caf50' : '#d32f2f';
                const icon = factor.contribution === 'positive'
                  ? <ThumbUpIcon sx={{ fontSize: '1.1rem' }} />
                  : <ThumbDownIcon sx={{ fontSize: '1.1rem' }} />;
                return (
                  <React.Fragment key={factor.id}>
                    <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', px: 0, py: 2 }}>
                      <Box sx={{ width: '100%', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
                          <Typography variant="body2" fontWeight="bold">{factor.label}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="grey.500">
                            {factor.dataSource} • {formatTs(factor.timestamp)}
                          </Typography>
                          <Typography variant="caption" color="grey.600" sx={{ fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                            Importance Weight: <span style={{ fontWeight: 700, color: '#424242' }}>{factor.importance}%</span>
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ width: '100%', mb: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={factor.importance}
                          sx={{
                            height: 8, borderRadius: 1, backgroundColor: 'grey.300',
                            '& .MuiLinearProgress-bar': { backgroundColor: color },
                          }}
                        />
                      </Box>
                      <Typography variant="body2">
                        <strong>Value:</strong> {factor.value}
                      </Typography>
                    </ListItem>
                    {idx < sorted.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
          {onSelect && !isSelected && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                variant={isRecommended ? 'contained' : 'outlined'}
                size="small"
                onClick={() => onSelect(disposition)}
                sx={{ textTransform: 'none' }}
              >
                Select
              </Button>
            </Box>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default DischargeProgramFitCard;
